import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { Copy, Download, History, Maximize, MessageSquare, Minimize, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArtifactDrawer } from '../../contexts/ArtifactDrawerContext';
import { useDrawerResize } from '../../hooks/useDrawerResize';
import { useGetDocument } from '../../hooks/useGetDocument';
import { useUpdateDocument } from '../../hooks/useUpdateDocument';
import { shanksConversationId } from '../../lib/chat/shanks';
import { useChatArtifactStore } from '../../lib/stores/ChatArtifactStore';
import { copyToClipboard, downloadText } from '../../utils/downloadUtils';
import { CompactChatSidebar } from '../ChatView/CompactChatSidebar';
import { DynamicEditor } from './DynamicEditor';
import { HistoryPanel } from './HistoryPanel';
import { SaveToProgramDocsButton } from './SaveToProgramDocsButton';
/**
 * Type guard to safely narrow ArtifactKind to supported document kinds
 * Ensures we only send kinds that the API schema accepts (text, code, image, sheet, presentation)
 * Filters out document and powerpoint which are frontend-only
 */ function isSupportedDocumentKind(kind) {
    return [
        'text',
        'code',
        'image',
        'sheet',
        'presentation'
    ].includes(kind);
}
/**
 * Artifact Editor Drawer component
 * Opens when a user clicks on an artifact preview to edit it
 * Displays the artifact with header (name, actions) and content area
 * In fullscreen mode, shows a compact chat sidebar for simultaneous editing and chatting
 */ export const ArtifactEditorDrawer = ({ isOpen, onClose, messages = [], isSending = false, chatId = '', onChatSubmit, onFeedback, onStop, readOnly = false })=>{
    const drawerRef = useRef(null);
    const [width, setWidth] = useState(560); // default width in pixels
    const [isEditingName, setIsEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [copiedSuccess, setCopiedSuccess] = useState(false);
    const [downloadSuccess, setDownloadSuccess] = useState(false);
    const navigate = useNavigate();
    // Get fullscreen state and source context from context
    const { isFullscreen, toggleFullscreen, sourceContext, sourceChatId } = useArtifactDrawer();
    const resizeHandleRef = useDrawerResize({
        minWidth: 300,
        maxWidth: 1400,
        defaultWidth: 560,
        onWidthChange: setWidth
    });
    const updateDocMutation = useUpdateDocument();
    // Reactive subscription to the selected artifact
    const selectedArtifact = useChatArtifactStore((state)=>state.selectedArtifact);
    const updateArtifact = useChatArtifactStore((state)=>state.updateArtifact);
    const setVersions = useChatArtifactStore((state)=>state.setVersions);
    const _getArtifact = useChatArtifactStore((state)=>state.getArtifact);
    const getDisplayArtifact = useChatArtifactStore((state)=>state.getDisplayArtifact);
    const _previewArtifact = useChatArtifactStore((state)=>state.previewArtifact);
    const clearPreview = useChatArtifactStore((state)=>state.clearPreview);
    const selectedId = selectedArtifact?.id ?? null;
    // Use display artifact which prefers preview over main artifact
    const artifact = selectedId ? getDisplayArtifact(selectedId) : null;
    // Always fetch document data to get latest content and version history
    // Even if we have content in the store, we need to fetch versions
    const shouldFetch = selectedId;
    const { data: documentData, isLoading } = useGetDocument(shouldFetch ? selectedId : null);
    // Clear preview when drawer closes OR when a different artifact is selected
    useEffect(()=>{
        if (!isOpen) {
            clearPreview();
        }
    }, [
        isOpen,
        clearPreview
    ]);
    // Clear preview when opening drawer or selecting new artifact
    // This ensures we always show the latest version when drawer first opens
    useEffect(()=>{
        if (isOpen && selectedId) {
            clearPreview();
        }
    }, [
        isOpen,
        selectedId,
        clearPreview
    ]);
    // Load document data and versions into store when data arrives
    useEffect(()=>{
        if (documentData && selectedId) {
            // Update artifact with latest document data
            if (documentData.latest) {
                // ALWAYS update main artifact to latest version when data arrives
                // This ensures the store always has the latest version as the source of truth
                updateArtifact(selectedId, {
                    title: documentData.latest.title,
                    content: documentData.latest.content,
                    updatedAt: documentData.latest.updatedAt
                });
            }
            // Store version history - always, even if empty
            setVersions(selectedId, documentData.versions || []);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        documentData,
        selectedId,
        setVersions,
        updateArtifact
    ]); // Only depend on data changes, not selector functions
    const handleNameEdit = useCallback(()=>{
        setIsEditingName(true);
    }, []);
    const handleNameSave = useCallback(()=>{
        if (editName.trim() && artifact && selectedId) {
            // Only update local store - name will be persisted when user clicks Save button
            updateArtifact(selectedId, {
                title: editName
            });
            setIsEditingName(false);
        }
    }, [
        editName,
        artifact,
        selectedId,
        updateArtifact
    ]);
    const handleSaveArtifact = useCallback(()=>{
        if (artifact && selectedId && isSupportedDocumentKind(artifact.kind)) {
            const update = {
                content: artifact.content,
                title: artifact.title,
                kind: artifact.kind
            };
            updateArtifact(selectedId, update);
            // Which chat this document belongs to, from whichever source knows: the
            // row itself (opened from the Artifacts tab), the chat we were opened
            // from, or the drawer's own chat context. Sending nothing is fine — the
            // server keeps the stored link rather than clearing it — but a NEW
            // document saved out of a live chat has no stored link yet, so this is
            // the only chance to record one.
            //
            // It must be the BARE conversation uuid. `shanks-<uuid>` is a frontend
            // wire form; the backend keys conversations on the bare id, and
            // chat_artifacts.chat_id is a varchar(36) sized for exactly that — the
            // 43-char prefixed form overflows it and the save dies with a 500.
            const rawChatId = artifact.chatId || sourceChatId || chatId || '';
            const bareChatId = shanksConversationId(rawChatId) ?? rawChatId;
            updateDocMutation.mutate({
                documentId: selectedId,
                payload: {
                    ...update,
                    chatId: bareChatId || undefined
                }
            });
        }
    }, [
        artifact,
        selectedId,
        updateArtifact,
        updateDocMutation,
        sourceChatId,
        chatId
    ]);
    const handleNameCancel = useCallback(()=>{
        setEditName(artifact?.title ?? '');
        setIsEditingName(false);
    }, [
        artifact?.title
    ]);
    const handleDownload = useCallback(()=>{
        if (artifact) {
            downloadText({
                title: artifact.title,
                content: artifact.content,
                kind: artifact.kind
            });
            setDownloadSuccess(true);
            setTimeout(()=>setDownloadSuccess(false), 1000);
        }
    }, [
        artifact
    ]);
    const handleViewInChat = useCallback(()=>{
        if (sourceChatId) {
            navigate(`/assistant/${sourceChatId}`);
            onClose();
        }
    }, [
        sourceChatId,
        navigate,
        onClose
    ]);
    // Initialize edit name when artifact changes
    useEffect(()=>{
        if (artifact) {
            setEditName(artifact.title);
        }
    }, [
        artifact
    ]);
    // Focus management: focus drawer when it opens
    useEffect(()=>{
        if (isOpen && drawerRef.current) {
            drawerRef.current.focus();
        }
    }, [
        isOpen
    ]);
    // Handle keyboard shortcuts
    const handleKeyDown = useCallback((e)=>{
        if (!isOpen) return;
        // Escape to close
        if (e.key === 'Escape') {
            if (isEditingName) {
                handleNameCancel();
            } else {
                onClose();
            }
        }
        // Cmd+S or Ctrl+S to save (will be implemented later)
        if ((e.metaKey || e.ctrlKey) && e.key === 's') {
            e.preventDefault();
        // TODO: Implement save functionality
        }
    }, [
        isOpen,
        onClose,
        isEditingName,
        handleNameCancel
    ]);
    useEffect(()=>{
        window.addEventListener('keydown', handleKeyDown);
        return ()=>window.removeEventListener('keydown', handleKeyDown);
    }, [
        handleKeyDown
    ]);
    // Memoize click-outside handler to prevent listener proliferation
    const handleClickOutside = useCallback((e)=>{
        if (!isEditingName) {
            return;
        }
        if (drawerRef.current?.contains(e.target)) {
            const titleElement = drawerRef.current.querySelector('#artifact-drawer-title');
            if (titleElement && !titleElement.contains(e.target)) {
                const inputElement = drawerRef.current.querySelector('input');
                if (inputElement && !inputElement.contains(e.target)) {
                    handleNameSave();
                }
            }
        }
    }, [
        isEditingName,
        handleNameSave
    ]);
    // Handle clicks outside the title input
    useEffect(()=>{
        if (!isEditingName) {
            return;
        }
        document.addEventListener('mousedown', handleClickOutside);
        return ()=>document.removeEventListener('mousedown', handleClickOutside);
    }, [
        isEditingName,
        handleClickOutside
    ]);
    // Clear save state after 2 seconds
    useEffect(()=>{
        if (updateDocMutation.isSuccess || updateDocMutation.isError) {
            const timer = setTimeout(()=>{
                updateDocMutation.reset();
            }, 2000);
            return ()=>clearTimeout(timer);
        }
    }, [
        updateDocMutation.isSuccess,
        updateDocMutation.isError,
        updateDocMutation
    ]);
    if (!isOpen || !artifact) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            !isFullscreen && /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-30 bg-black/20",
                style: {
                    pointerEvents: 'auto'
                },
                onClick: onClose,
                "aria-hidden": "true"
            }),
            /*#__PURE__*/ _jsxs("div", {
                ref: drawerRef,
                className: `fixed z-40 shadow-xl flex will-change-transform ${isFullscreen ? 'inset-0 flex-row' : 'inset-y-0 right-0 flex-col bg-background border-l'}`,
                style: isFullscreen ? undefined : {
                    width: `${width}px`,
                    pointerEvents: 'auto'
                },
                role: "dialog",
                "aria-modal": "true",
                "aria-labelledby": "artifact-drawer-title",
                tabIndex: -1,
                "data-testid": Testids.artifacts.drawer,
                children: [
                    isFullscreen && onChatSubmit && /*#__PURE__*/ _jsx(CompactChatSidebar, {
                        messages: messages,
                        isSending: isSending,
                        chatId: chatId,
                        onSubmit: onChatSubmit,
                        onFeedback: onFeedback,
                        onStop: onStop,
                        readOnly: readOnly
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: `flex-1 flex flex-col bg-background h-full relative ${isFullscreen ? '' : 'border-l'}`,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between h-12 px-3 border-b",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "flex-1 min-w-0",
                                        children: isEditingName ? /*#__PURE__*/ _jsx("input", {
                                            value: editName,
                                            onChange: (e)=>setEditName(e.target.value),
                                            onKeyDown: (e)=>{
                                                if (e.key === 'Enter') handleNameSave();
                                                if (e.key === 'Escape') handleNameCancel();
                                            },
                                            className: "w-full px-2 py-1 text-sm bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
                                        }) : /*#__PURE__*/ _jsx("div", {
                                            id: "artifact-drawer-title",
                                            onClick: handleNameEdit,
                                            className: "text-sm font-medium truncate",
                                            children: artifact.title || 'Document'
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2 text-muted-foreground",
                                        children: [
                                            sourceContext === 'artifacts-page' && sourceChatId && /*#__PURE__*/ _jsx("button", {
                                                title: "View in Chat",
                                                className: "p-1 rounded hover:bg-muted transition-colors",
                                                type: "button",
                                                onClick: handleViewInChat,
                                                "aria-label": "View in chat",
                                                children: /*#__PURE__*/ _jsx(MessageSquare, {
                                                    className: "h-4 w-4"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                title: "View changes",
                                                className: `p-1 rounded hover:bg-muted transition-colors ${showHistory ? 'bg-muted text-foreground' : ''}`,
                                                type: "button",
                                                onClick: ()=>setShowHistory(!showHistory),
                                                "aria-label": "View history",
                                                children: /*#__PURE__*/ _jsx(History, {
                                                    className: "h-4 w-4"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                title: updateDocMutation.error?.message || 'Save',
                                                className: `p-1 rounded flex items-center justify-center transition-all duration-300 ${updateDocMutation.isPending ? 'opacity-60 cursor-not-allowed' : updateDocMutation.isSuccess ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : updateDocMutation.isError ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400' : 'hover:bg-muted'}`,
                                                type: "button",
                                                onClick: handleSaveArtifact,
                                                disabled: updateDocMutation.isPending,
                                                "aria-label": "Save artifact",
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: "text-xs",
                                                    children: updateDocMutation.isPending ? '...' : updateDocMutation.isSuccess ? '✓' : updateDocMutation.isError ? '✕' : 'Save'
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SaveToProgramDocsButton, {
                                                artifact: artifact
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                title: "Copy",
                                                className: `p-1 rounded transition-all duration-300 flex items-center justify-center w-6 h-6 ${copiedSuccess ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : 'hover:bg-muted'}`,
                                                type: "button",
                                                onClick: async ()=>{
                                                    if (artifact?.content) {
                                                        const success = await copyToClipboard(artifact.content);
                                                        if (success) {
                                                            setCopiedSuccess(true);
                                                            setTimeout(()=>setCopiedSuccess(false), 1000);
                                                        }
                                                    }
                                                },
                                                "aria-label": "Copy artifact",
                                                children: copiedSuccess ? /*#__PURE__*/ _jsx("span", {
                                                    className: "text-sm",
                                                    children: "✓"
                                                }) : /*#__PURE__*/ _jsx(Copy, {
                                                    className: "h-4 w-4"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                title: "Download",
                                                className: `p-1 rounded transition-all duration-300 flex items-center justify-center w-6 h-6 ${downloadSuccess ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : 'hover:bg-muted'}`,
                                                type: "button",
                                                onClick: handleDownload,
                                                "aria-label": "Download artifact",
                                                children: downloadSuccess ? /*#__PURE__*/ _jsx("span", {
                                                    className: "text-sm",
                                                    children: "✓"
                                                }) : /*#__PURE__*/ _jsx(Download, {
                                                    className: "h-4 w-4"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                title: isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen',
                                                className: "p-1 rounded hover:bg-muted",
                                                type: "button",
                                                onClick: toggleFullscreen,
                                                "aria-label": isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen',
                                                children: isFullscreen ? /*#__PURE__*/ _jsx(Minimize, {
                                                    className: "h-4 w-4"
                                                }) : /*#__PURE__*/ _jsx(Maximize, {
                                                    className: "h-4 w-4"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                title: "Close",
                                                className: "p-1 rounded hover:bg-muted",
                                                type: "button",
                                                onClick: onClose,
                                                "aria-label": "Close drawer",
                                                children: /*#__PURE__*/ _jsx(X, {
                                                    className: "h-4 w-4"
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(HistoryPanel, {
                                isOpen: showHistory,
                                selectedId: selectedId,
                                isLoading: isLoading,
                                onVersionSelect: ()=>setShowHistory(false)
                            }),
                            !showHistory && /*#__PURE__*/ _jsx("div", {
                                className: "flex-1 overflow-y-auto overflow-x-hidden",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "p-4",
                                    children: isLoading && !artifact?.content ? /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-full"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-5/6"
                                            })
                                        ]
                                    }) : artifact && selectedId ? /*#__PURE__*/ _jsx(DynamicEditor, {
                                        artifact: artifact,
                                        onChange: (content)=>{
                                            updateArtifact(selectedId, {
                                                title: artifact.title,
                                                content,
                                                kind: artifact.kind
                                            });
                                        }
                                    }) : null
                                })
                            }),
                            !isFullscreen && /*#__PURE__*/ _jsx("button", {
                                ref: resizeHandleRef,
                                className: "absolute left-0 top-0 h-full w-1.5 cursor-col-resize group hover:bg-blue-500 hover:bg-opacity-30 focus:outline-none focus:ring-1 focus:ring-blue-500 p-0 border-none bg-transparent",
                                "aria-label": "Resize drawer",
                                type: "button",
                                onKeyDown: (e)=>{
                                    // Handle arrow keys for keyboard resizing if needed
                                    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                                        e.preventDefault();
                                    }
                                },
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "w-1 h-full bg-border group-hover:bg-blue-500 group-hover:bg-opacity-50"
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
